import React, { ReactElement, useEffect, useState } from 'react';
import { object as yObject } from 'yup';
import { FormikContainerOld } from 'client/modules/common/form/containers/FormikContainerOld';
import { useUserEvents } from 'client/utils/user-event/hooks/useUserEvent';
import {
  DealCard,
  DealQueueData,
  DealQueueFilters,
  getDealQueue,
} from '../apis/GetDealQueue';
import { clearDealQueue } from '../apis/ClearDealQueue';
import { recordDealOutcome } from '../apis/RecordDealOutcome';
import {
  DealFilterOption,
  getDealFilterOptions,
} from '../apis/GetDealFilterOptions';
import {
  COMPANY_CATEGORY_OPTIONS,
  DATE_FIELD_OPTIONS,
  DEAL_OUTCOME,
  DEAL_REVIEW_CHANNEL,
  DEAL_TYPE_OPTIONS,
  GROUP_BY_OPTIONS,
  SORT_BY_DATE_OPTIONS,
} from '../utils/dealConstants';
import { DealQueue } from './DealQueue';
import { UpdateMergeOptions, UpdateTarget } from '../utils/dealUpdate';

const FilterSchema = yObject().shape({});

// Messages from the funding tab meaning "this deal is finalized, drop its card" (see FundingDrafts).
const FINALIZED_BROADCAST_TYPES = ['published', 'drafted', 'merged'];

interface FilterOption {
  label: string;
  value: number;
}

interface DealFilterValues {
  dealType: FilterOption;
  groupBy: FilterOption;
  sortByDate: FilterOption;
  dateField: FilterOption;
  companyCategory: FilterOption;
  language: FilterOption[]; // multi-select; empty = no language filter (all languages)
  startDate: string;
  endDate: string;
  idCbiEntity: string; // comma-separated id_cbi_entity values
  idSource: string; // comma-separated cb_company_news.id_source values
  hidePublished: boolean; // hide deals already linked to a published funding
  primaryDealsOnly: boolean; // only deals flagged as the article's main deal
  amountMin: string;
  amountMax: string;
  limit: string; // how many deals to claim per queue
}

const DEFAULT_LIMIT = 25;
const MAX_LIMIT = 500;

export const defaultDealFilters: DealFilterValues = {
  dealType: DEAL_TYPE_OPTIONS[0], // All
  groupBy: GROUP_BY_OPTIONS[1], // Company
  sortByDate: SORT_BY_DATE_OPTIONS[0], // oldest first
  dateField: DATE_FIELD_OPTIONS[0], // Deal date
  companyCategory: COMPANY_CATEGORY_OPTIONS[0], // All companies
  language: [], // no language filter -> all languages
  hidePublished: true, // hide already-published deals by default; untick to see those that differ (with a note)
  primaryDealsOnly: false, // include non-primary deals unless toggled
  startDate: '',
  endDate: '',
  idCbiEntity: '',
  idSource: '',
  amountMin: '',
  amountMax: '',
  limit: String(DEFAULT_LIMIT),
};

function parseIds(raw: string): number[] {
  return (raw || '')
    .split(',')
    .map((s) => parseInt(s.trim(), 10))
    .filter((n) => Number.isFinite(n) && n > 0);
}

function parseAmount(raw: string): number {
  const n = parseFloat(raw);
  return Number.isFinite(n) && n > 0 ? n : 0; // 0 -> ignored by the backend
}

function parseLimit(raw: string): number {
  const n = parseInt(raw, 10);
  if (!Number.isFinite(n) || n <= 0) {
    return DEFAULT_LIMIT;
  }
  return Math.min(n, MAX_LIMIT);
}

function toFilterRequest(values: DealFilterValues): DealQueueFilters {
  return {
    dealType: values.dealType?.value ?? 0,
    groupBy: values.groupBy?.value ?? 0,
    sortByDate: values.sortByDate?.value ?? 1,
    dateField: values.dateField?.value ?? 1,
    companyCategory: values.companyCategory?.value ?? 0,
    hidePublished: Boolean(values.hidePublished),
    primaryDealsOnly: Boolean(values.primaryDealsOnly),
    startDate: values.startDate || '',
    endDate: values.endDate || '',
    idCbiEntity: parseIds(values.idCbiEntity),
    idSource: parseIds(values.idSource),
    amountMin: parseAmount(values.amountMin),
    amountMax: parseAmount(values.amountMax),
    // Multi-select -> repeated field; empty array means no language filter (all languages).
    languages: (Array.isArray(values.language) ? values.language : [])
      .map((option) => option.value)
      .filter((value) => value > 0),
  };
}

export const DealQueueContainer = (): ReactElement => {
  const [dealData, setDealData] = useState<DealQueueData>({
    dealCards: [],
    totalCount: 0,
    isLoading: true,
  });
  const [languageOptions, setLanguageOptions] = useState<DealFilterOption[]>(
    []
  );
  const { recordUserEvent } = useUserEvents();

  // Filter options rarely change; fetch once on mount (backend caches them for a day).
  useEffect(() => {
    getDealFilterOptions().then((options) =>
      setLanguageOptions(options.languageOptions)
    );
  }, []);

  const fetchDeals = (values: DealFilterValues) => {
    setDealData((state) => ({ ...state, isLoading: true }));
    getDealQueue(toFilterRequest(values), parseLimit(values.limit))
      .then(setDealData)
      .catch(() =>
        setDealData({ dealCards: [], totalCount: 0, isLoading: false })
      );
  };

  useEffect(() => {
    fetchDeals(defaultDealFilters);
  }, []);

  // Cross-tab sync: Publish opens the funding admin in a NEW tab, so a deal published there is
  // finalized server-side but this queue tab wouldn't know. The funding tab broadcasts the published
  // id_news_deal (see FundingDrafts); remove that card here so it doesn't linger as already-done.
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') {
      return undefined;
    }
    const channel = new BroadcastChannel(DEAL_REVIEW_CHANNEL);
    channel.onmessage = (event) => {
      const { type, idNewsDeal } = event.data || {};
      if (FINALIZED_BROADCAST_TYPES.includes(type) && idNewsDeal) {
        setDealData((state) => ({
          ...state,
          dealCards: state.dealCards.filter(
            (card) => card.idNewsDeal !== idNewsDeal
          ),
        }));
      }
    };
    return () => channel.close();
  }, []);

  const onFilterSubmit = (values: DealFilterValues) => {
    recordUserEvent({
      type: 'admin.dq.fetchDeals',
      details: toFilterRequest(values),
    });
    return getDealQueue(toFilterRequest(values), parseLimit(values.limit))
      .then(setDealData)
      .catch(() =>
        setDealData({ dealCards: [], totalCount: 0, isLoading: false })
      );
  };

  const removeCard = (idNewsDeal: number) => {
    setDealData((state) => ({
      ...state,
      dealCards: state.dealCards.filter(
        (card) => card.idNewsDeal !== idNewsDeal
      ),
    }));
  };

  const onReject = (card: DealCard) => {
    recordUserEvent({
      type: 'admin.dq.reject',
      details: { idNewsDeal: card.idNewsDeal },
    });
    return recordDealOutcome({
      idNewsDeal: card.idNewsDeal,
      outcome: DEAL_OUTCOME.REJECTED,
    })
      .then((success) => success && removeCard(card.idNewsDeal))
      .catch(() => {
        // best-effort; recordDealOutcome already surfaces a toast on failure
      });
  };

  const onSkip = (card: DealCard) => {
    recordUserEvent({
      type: 'admin.dq.skip',
      details: { idNewsDeal: card.idNewsDeal },
    });
    return recordDealOutcome({
      idNewsDeal: card.idNewsDeal,
      outcome: DEAL_OUTCOME.SKIPPED,
    })
      .then((success) => success && removeCard(card.idNewsDeal))
      .catch(() => {
        // best-effort; recordDealOutcome already surfaces a toast on failure
      });
  };

  const onPublish = (card: DealCard) => {
    // An unresolved entity (idCbiEntity <= 0) would open /cbi-entity/0/fundings, a dead end.
    // The Publish button is disabled in that case, but guard here too against programmatic calls.
    if (card.anchor.idCbiEntity <= 0) {
      return;
    }
    recordUserEvent({
      type: 'admin.dq.publishClick',
      details: { idNewsDeal: card.idNewsDeal },
    });
    // Hand the deal's extracted data to the funding admin via localStorage (survives the new tab).
    try {
      window.localStorage.setItem(
        `deal_prefill:${card.idNewsDeal}`,
        JSON.stringify({ anchor: card.anchor, memberDeals: card.memberDeals })
      );
    } catch {
      // localStorage may be unavailable; the funding admin simply won't prefill.
    }
    const win = window.open(
      `/cbi-entity/${card.anchor.idCbiEntity}/fundings?id_news_deal=${card.idNewsDeal}`,
      '_blank'
    );
    if (!win) {
      window.alert(
        'Could not open the funding page, please disable your popup blocker'
      );
    }
  };

  // Update: merge this deal into an existing funding/draft instead of creating a new draft. Same handoff
  // as Publish, plus the chosen target + what to bring over; opens the TARGET's entity (which may differ
  // from the deal's own when the reviewer picked "Other funding").
  const onUpdate = (
    card: DealCard,
    target: UpdateTarget,
    merge: UpdateMergeOptions
  ) => {
    if (target.idCbiEntity <= 0) {
      return;
    }
    recordUserEvent({
      type: 'admin.dq.updateClick',
      details: {
        idNewsDeal: card.idNewsDeal,
        idFunding: target.idFunding,
        idFundingDraft: target.idFundingDraft,
        merge,
      },
    });
    try {
      window.localStorage.setItem(
        `deal_prefill:${card.idNewsDeal}`,
        JSON.stringify({
          anchor: card.anchor,
          memberDeals: card.memberDeals,
          update: { target, merge },
        })
      );
    } catch {
      // localStorage may be unavailable; the funding admin simply won't merge.
    }
    // `draft=` / `funding=` make the funding page scroll to and outline the row being updated.
    const params = new URLSearchParams({
      id_news_deal: String(card.idNewsDeal),
    });
    if (target.idFundingDraft) {
      params.set('draft', target.idFundingDraft);
    } else {
      params.set('funding', String(target.idFunding));
    }
    const win = window.open(
      `/cbi-entity/${target.idCbiEntity}/fundings?${params.toString()}`,
      '_blank'
    );
    if (!win) {
      window.alert(
        'Could not open the funding page, please disable your popup blocker'
      );
    }
  };

  const onClearQueue = () => {
    recordUserEvent({ type: 'admin.dq.clearQueue' });
    setDealData((state) => ({ ...state, isLoading: true }));
    return clearDealQueue()
      .then((success) => {
        if (success) {
          setDealData({ dealCards: [], totalCount: 0, isLoading: false });
        } else {
          setDealData((state) => ({ ...state, isLoading: false }));
        }
      })
      .catch(() => setDealData((state) => ({ ...state, isLoading: false })));
  };

  return (
    <div className="container mx-auto mt-8 mb-4">
      <div className="text-center text-3xl text-black font-medium py-2">
        Deal Review Queue
      </div>
      <div className="mt-4">
        <FormikContainerOld
          enableReinitialize
          Form={DealQueue}
          schema={FilterSchema}
          onSubmit={onFilterSubmit}
          handleClear={onClearQueue}
          initialValues={defaultDealFilters}
          dealData={dealData}
          languageOptions={languageOptions}
          customContext={{ onReject, onSkip, onPublish, onUpdate }}
        />
      </div>
    </div>
  );
};

export default DealQueueContainer;
